import React, { useState, useEffect } from 'react';
import automatedPaymentService from '../../../services/automatedPaymentService';

const RetryLogModal = ({ payment, onClose }) => {
  const [retryLogs, setRetryLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRetryLogs();
  }, [payment?.id]);

  const fetchRetryLogs = async () => {
    try {
      setLoading(true);
      const logs = await automatedPaymentService?.getPaymentRetryLogs(payment?.id);
      setRetryLogs(logs);
    } catch (error) {
      console.error('Error fetching retry logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString('en-PG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const display = automatedPaymentService?.getStatusDisplay(status);
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${display?.color}`}>
        {display?.icon} {display?.label}
      </span>
    );
  };

  const formatDelay = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Payment Retry Logs</h2>
              <p className="mt-1 text-sm text-gray-600">
                User: {payment?.userFullName} ({payment?.userEmail})
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Payment Details */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase">Amount</p>
              <p className="text-lg font-semibold text-gray-900">
                {automatedPaymentService?.formatAmount(payment?.amount, payment?.currency)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Status</p>
              <div className="mt-1">{getStatusBadge(payment?.status)}</div>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Retry Count</p>
              <p className="text-lg font-semibold text-gray-900">
                {payment?.retryCount} / {payment?.maxRetries}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Scheduled Charge</p>
              <p className="text-sm text-gray-900">
                {formatDate(payment?.scheduledChargeDate)}
              </p>
            </div>
          </div>
          {payment?.errorMessage && (
            <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-500 rounded">
              <p className="text-xs text-red-600 font-semibold">Latest Error:</p>
              <p className="text-sm text-red-700">{payment?.errorMessage}</p>
            </div>
          )}
        </div>

        {/* Retry Logs */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 300px)' }}>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading retry logs...</p>
            </div>
          ) : retryLogs?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No retry attempts yet
            </div>
          ) : (
            <div className="space-y-4">
              {retryLogs?.map((log, index) => (
                <div
                  key={log?.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold text-sm">
                        {log?.retryAttempt}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Retry Attempt #{log?.retryAttempt}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(log?.createdAt)}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(log?.status)}
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {log?.errorCode && (
                      <div>
                        <p className="text-gray-500">Error Code:</p>
                        <p className="font-medium text-gray-900">{log?.errorCode}</p>
                      </div>
                    )}
                    {log?.retryDelaySeconds && (
                      <div>
                        <p className="text-gray-500">Retry Delay:</p>
                        <p className="font-medium text-gray-900">
                          {formatDelay(log?.retryDelaySeconds)}
                        </p>
                      </div>
                    )}
                    {log?.nextRetryScheduledAt && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Next Retry Scheduled:</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(log?.nextRetryScheduledAt)}
                        </p>
                      </div>
                    )}
                  </div>

                  {log?.errorMessage && (
                    <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-400 rounded">
                      <p className="text-xs text-red-600 font-semibold">Error Message:</p>
                      <p className="text-sm text-red-700">{log?.errorMessage}</p>
                    </div>
                  )}

                  {log?.stripePaymentIntentId && (
                    <div className="mt-3 text-xs text-gray-500">
                      Payment Intent: {log?.stripePaymentIntentId}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RetryLogModal;
